import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Heading, Button, Spinner, Center, Progress,
  VStack, HStack, Badge,
} from '@chakra-ui/react';
import { UserAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  DealerTrack, DealerTier, ScreenerResult, DealerQuestion,
  HOME_TEST_CONFIG, PROFESSIONAL_TEST_CONFIG, SCREENER_CONFIG,
  TIER_BADGES, CATEGORY_NAMES,
  drawScreener, drawHomeTest, drawProfessionalTest,
  scoreScreener, scoreTest,
} from '../../constants/dealerQuestions';

// ── TYPES ──────────────────────────────────────────────────────────────────

type Phase =
  | 'loading'
  | 'already_certified'
  | 'track_select'
  | 'questionnaire'
  | 'screener'
  | 'screener_result'
  | 'test'
  | 'test_result'
  | 'wrong_answer'
  | 'certified';

interface QuestionnaireAnswers {
  age_confirmed: boolean;
  track: DealerTrack;
  has_experience: boolean;
  knows_tda: boolean;
  event_count: string;
  largest_event: string;
  games: string[];
  available: boolean;
  travel_radius: string;
  conduct_issue: boolean;
  city: string;
  travel_radius_km: number;
}

// ── HELPERS ────────────────────────────────────────────────────────────────

const F = { ui: "'Rajdhani', 'Arial', sans-serif", mono: "'Share Tech Mono', monospace" };
const C = {
  bg: '#080A0E', card: '#131720', border: '#1E2330',
  gold: '#D4A017', teal: '#19E6CF', white: '#F0F4FF',
  ink: '#C8D0E0', dim: '#6B7590', green: '#22C55E',
  red: '#EF4444', purple: '#7C3AED',
};

const wrap = {
  maxW: '640px', mx: 'auto', px: 4, py: 8,
  fontFamily: F.ui,
} as const;

const cardStyle: React.CSSProperties = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  overflow: 'hidden',
};

const headerStyle = (color = C.teal): React.CSSProperties => ({
  background: '#0D1F1E',
  padding: '14px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${C.border}`,
});

const btn = (bg: string, color: string, border?: string): React.CSSProperties => ({
  background: bg, color, border: `1px solid ${border ?? bg}`,
  borderRadius: 8, padding: '10px 20px', fontSize: 14,
  fontFamily: F.ui, fontWeight: 700, cursor: 'pointer',
  letterSpacing: '0.06em',
});

const optionStyle = (selected: boolean): React.CSSProperties => ({
  background: selected ? 'rgba(25,230,207,0.1)' : C.card,
  border: `2px solid ${selected ? C.teal : C.border}`,
  borderRadius: 8, padding: '12px 16px',
  cursor: 'pointer', transition: 'all 0.15s',
  color: selected ? C.teal : C.ink,
  fontSize: 14, fontFamily: F.ui,
  marginBottom: 8,
});

// ── COMPONENT ──────────────────────────────────────────────────────────────

const DealerCertification: FC = () => {
  const { user } = UserAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('loading');
  const [track, setTrack] = useState<DealerTrack>('professional');
  const [tier, setTier] = useState<DealerTier>('none');
  const [existingData, setExistingData] = useState<any>(null);

  // Questionnaire
  const [qStep, setQStep] = useState(0);
  const [qAnswers, setQAnswers] = useState<Partial<QuestionnaireAnswers>>({});

  // Screener
  const [screenerQs, setScreenerQs] = useState<DealerQuestion[]>([]);
  const [screenerAnswers, setScreenerAnswers] = useState<number[]>([]);
  const [screenerIndex, setScreenerIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [screenerResult, setScreenerResult] = useState<any>(null);

  // Test
  const [testQs, setTestQs] = useState<DealerQuestion[]>([]);
  const [testAnswers, setTestAnswers] = useState<number[]>([]);
  const [testIndex, setTestIndex] = useState(0);
  const [wrongQ, setWrongQ] = useState<DealerQuestion | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [attemptNumber, setAttemptNumber] = useState(1);

  // ── LOAD existing dealer data ────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      if (!user?.uid) return;
      try {
        const snap = await getDoc(doc(db, 'dealers', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setExistingData(data);
          setTier(data.tier ?? 'none');
          setTrack(data.track ?? 'professional');
          if (data.tier && data.tier !== 'none') {
            setPhase('already_certified');
            return;
          }
        }
        setPhase('track_select');
      } catch (e) {
        console.error(e);
        setPhase('track_select');
      }
    };
    load();
  }, [user]);

  // ── QUESTIONNAIRE LOGIC ──────────────────────────────────────────────────

  const questionnaireSteps = track === 'professional' ? [
    {
      id: 'age', label: 'Are you 18 years of age or older?',
      options: ['Yes, I am 18 or older', 'No, I am under 18'],
      gate: (v: number) => v === 0,
      gateMsg: 'You must be 18 or older to apply as a dealer on PokerCity.',
      field: 'age_confirmed', mapValue: (v: number) => v === 0,
    },
    {
      id: 'experience', label: 'Have you dealt poker in a structured environment (tournament or cash game)?',
      options: ['Yes — I have dealing experience', 'No — I am new to dealing'],
      gate: (v: number) => v === 0,
      gateMsg: 'The Professional track requires prior dealing experience. Consider the Home Game track to get started.',
      field: 'has_experience', mapValue: (v: number) => v === 0,
    },
    {
      id: 'tda', label: 'Do you have knowledge of TDA tournament rules?',
      options: ['Yes — I am familiar with TDA rules', 'No — I am not familiar with TDA rules'],
      gate: (v: number) => v === 0,
      gateMsg: 'TDA rules knowledge is required for the Professional track. We recommend reviewing the TDA rulebook before applying.',
      field: 'knows_tda', mapValue: (v: number) => v === 0,
    },
    {
      id: 'events', label: 'How many tournaments have you dealt?',
      options: ['None', '1 – 10', '11 – 50', '50+'],
      gate: null,
      field: 'event_count', mapValue: (v: number) => ['0', '1-10', '11-50', '50+'][v],
    },
    {
      id: 'size', label: 'Largest event you have dealt by player count?',
      options: ['Under 20 players', '20 – 50 players', '50 – 100 players', '100+ players'],
      gate: null,
      field: 'largest_event', mapValue: (v: number) => ['under_20', '20-50', '50-100', '100+'][v],
    },
    {
      id: 'conduct', label: 'Have you ever been suspended or removed from a dealing position for conduct reasons?',
      options: ['No', 'Yes'],
      gate: null,
      field: 'conduct_issue', mapValue: (v: number) => v === 1,
      flagIfYes: true,
    },
    {
      id: 'city', label: 'What city or town are you based in?',
      options: [], // text input — handled specially
      gate: null,
      field: 'city', mapValue: (v: number) => v, // unused for text steps
      isTextInput: true,
    },
    {
      id: 'travel_radius', label: 'How far are you willing to travel to deal an event?',
      options: ['Up to 25 km', 'Up to 50 km', 'Up to 100 km', 'Province-wide'],
      gate: null,
      field: 'travel_radius_km', mapValue: (v: number) => [25, 50, 100, 999][v],
    },
  ] : [
    {
      id: 'age', label: 'Are you 18 years of age or older?',
      options: ['Yes, I am 18 or older', 'No, I am under 18'],
      gate: (v: number) => v === 0,
      gateMsg: 'You must be 18 or older to register as a dealer on PokerCity.',
      field: 'age_confirmed', mapValue: (v: number) => v === 0,
    },
    {
      id: 'rules', label: 'Do you understand the basic rules of Texas Hold\'em poker?',
      options: ['Yes — I know the rules well', 'Not really — I\'m still learning'],
      gate: (v: number) => v === 0,
      gateMsg: 'Basic Texas Hold\'em knowledge is required. Complete the Rules Certification on your Passport first.',
      field: 'knows_rules', mapValue: (v: number) => v === 0,
    },
    {
      id: 'experience', label: 'Have you dealt a home game before?',
      options: ['Yes', 'No — this would be my first time'],
      gate: null,
      field: 'has_experience', mapValue: (v: number) => v === 0,
    },
    {
      id: 'events', label: 'Approximately how many home games have you dealt?',
      options: ['None yet', '1 – 5 games', '6 – 20 games', '20+ games'],
      gate: null,
      field: 'event_count', mapValue: (v: number) => ['0', '1-5', '6-20', '20+'][v],
    },
    {
      id: 'conduct', label: 'Have you ever been asked to leave a game for conduct reasons?',
      options: ['No', 'Yes'],
      gate: null,
      field: 'conduct_issue', mapValue: (v: number) => v === 1,
    },
    {
      id: 'city', label: 'What city or town are you based in?',
      options: [],
      gate: null,
      field: 'city', mapValue: (v: number) => v,
      isTextInput: true,
    },
    {
      id: 'travel_radius', label: 'How far are you willing to travel to deal a game?',
      options: ['Up to 25 km', 'Up to 50 km', 'Up to 100 km', 'Province-wide'],
      gate: null,
      field: 'travel_radius_km', mapValue: (v: number) => [25, 50, 100, 999][v],
    },
  ];

  const currentStep = questionnaireSteps[qStep];
  const [qSelected, setQSelected] = useState<number | null>(null);
  const [gateBlocked, setGateBlocked] = useState<string | null>(null);
  const [cityInput, setCityInput] = useState('');

  const submitQStep = () => {
    if (qSelected === null || !currentStep) return;
    const step = currentStep;
    if (step.gate && !step.gate(qSelected)) {
      setGateBlocked(step.gateMsg);
      return;
    }
    const newAnswers = { ...qAnswers, [step.field]: step.mapValue(qSelected) };
    setQAnswers(newAnswers);
    setQSelected(null);

    if (qStep >= questionnaireSteps.length - 1) {
      // Done with questionnaire — start screener
      const qs = drawScreener();
      setScreenerQs(qs);
      setScreenerAnswers([]);
      setScreenerIndex(0);
      setSelectedAnswer(null);
      setPhase('screener');
    } else {
      setQStep(q => q + 1);
    }
  };

  // ── SCREENER LOGIC ───────────────────────────────────────────────────────

  const submitScreenerAnswer = () => {
    if (selectedAnswer === null) return;
    const newAnswers = [...screenerAnswers, selectedAnswer];
    setScreenerAnswers(newAnswers);

    if (screenerIndex >= screenerQs.length - 1) {
      // Score screener
      const result = scoreScreener(screenerQs, newAnswers);
      setScreenerResult(result);
      setPhase('screener_result');
    } else {
      setScreenerIndex(i => i + 1);
      setSelectedAnswer(null);
    }
  };

  const startTest = (chosenTrack: DealerTrack) => {
    setTrack(chosenTrack);
    const qs = chosenTrack === 'home' ? drawHomeTest() : drawProfessionalTest();
    setTestQs(qs);
    setTestAnswers([]);
    setTestIndex(0);
    setSelectedAnswer(null);
    setPhase('test');
  };

  // ── TEST LOGIC ───────────────────────────────────────────────────────────

  const submitTestAnswer = () => {
    if (selectedAnswer === null) return;
    const q = testQs[testIndex];
    const newAnswers = [...testAnswers, selectedAnswer];

    if (selectedAnswer !== q.correctIndex) {
      // Wrong answer — show explanation (like Rules Cert reading phase)
      setWrongQ(q);
      setTestAnswers(newAnswers);
      setPhase('wrong_answer');
      return;
    }

    if (testIndex >= testQs.length - 1) {
      // All answered — score
      finishTest(newAnswers);
    } else {
      setTestAnswers(newAnswers);
      setTestIndex(i => i + 1);
      setSelectedAnswer(null);
    }
  };

  const retryAfterReading = () => {
    setWrongQ(null);
    setSelectedAnswer(null);
    setPhase('test');
    // Don't advance — the question stays the same but we re-prompt
    // Actually we draw a fresh test to ensure randomness on retry
  };

  const finishTest = async (answers: number[]) => {
    const config = track === 'home' ? HOME_TEST_CONFIG : PROFESSIONAL_TEST_CONFIG;
    const result = scoreTest(testQs, answers, config, attemptNumber);
    setTestResult(result);

    if (result.passed && user?.uid) {
      const newTier: DealerTier = track === 'home' ? 'home_certified' : 'pc_certified';
      setTier(newTier);
      try {
        // Geocode city to lat/lng
        let home_lat = null;
        let home_lng = null;
        let home_city = (qAnswers as any).city || '';
        try {
          if (home_city) {
            const geoResp = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(home_city)}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
            );
            const geoData = await geoResp.json();
            if (geoData.results?.[0]?.geometry?.location) {
              home_lat = geoData.results[0].geometry.location.lat;
              home_lng = geoData.results[0].geometry.location.lng;
              home_city = geoData.results[0].formatted_address;
            }
          }
        } catch {}

        await setDoc(doc(db, 'dealers', user.uid), {
          track,
          tier: newTier,
          status: (qAnswers as any).conduct_issue ? 'pending' : 'active',
          age_confirmed: true,
          questionnaire: qAnswers,
          test_results: {
            score: result.score,
            passed: true,
            attempts: attemptNumber,
            completedAt: serverTimestamp(),
          },
          availability: 'available',
          games: ['holdem'],
          event_count: 0,
          avg_rating: 0,
          rating_count: 0,
          endorsements: [],
          home_city,
          home_lat,
          home_lng,
          travel_radius_km: (qAnswers as any).travel_radius_km ?? 50,
          travel_zones: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (e) { console.error(e); }
      setPhase('certified');
    } else {
      setPhase('test_result');
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return <Center h="400px"><Spinner size="xl" color="teal.400" /></Center>;
  }

  // ── ALREADY CERTIFIED ──────────────────────────────────────────────────
  if (phase === 'already_certified') {
    const badge = TIER_BADGES[tier];
    return (
      <Box {...wrap}>
        <div style={cardStyle}>
          <div style={headerStyle()}>
            <Text style={{ fontFamily: F.ui, fontSize: 14, fontWeight: 700, color: C.teal, letterSpacing: '0.15em' }}>
              DEALER PASSPORT
            </Text>
            <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.dim, letterSpacing: '0.2em' }}>
              {badge.icon} {badge.label}
            </Text>
          </div>
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            <Text style={{ fontSize: 48, marginBottom: 8 }}>{badge.icon}</Text>
            <Heading style={{ fontFamily: F.ui, color: C.white, fontSize: 24, marginBottom: 8 }}>
              You are a {badge.label}
            </Heading>
            <Text style={{ color: C.dim, fontSize: 14, marginBottom: 24 }}>
              Your dealer certification is active. View your public Dealer Passport or return to the dashboard.
            </Text>
            <Flex gap={3}>
              <Button flex={1} variant="outline" colorScheme="teal" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
              <Button flex={1} colorScheme="teal" onClick={() => user && navigate(`/dealer/${user.uid}`)}>
                View Dealer Passport
              </Button>
            </Flex>
          </div>
        </div>
      </Box>
    );
  }

  // ── TRACK SELECT ───────────────────────────────────────────────────────
  if (phase === 'track_select') {
    return (
      <Box {...wrap}>
        <div style={cardStyle}>
          <div style={headerStyle()}>
            <span style={{ fontFamily: F.ui, color: C.white, fontSize: 16, fontWeight: 700 }}>
              poker<span style={{ color: C.gold }}>city</span>♣
            </span>
            <span style={{ fontFamily: F.mono, fontSize: 11, color: C.teal, letterSpacing: '0.2em' }}>
              DEALER PASSPORT
            </span>
          </div>
          <div style={{ padding: '28px 24px' }}>
            <Heading style={{ fontFamily: F.ui, color: C.white, fontSize: 22, marginBottom: 8 }}>
              Become a Dealer
            </Heading>
            <Text style={{ color: C.dim, fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              Choose your goal. You can upgrade from Home Game to Professional later.
            </Text>

            <VStack gap={4} align="stretch">
              {/* Home Game Track */}
              <div
                style={{
                  background: '#0A1A10', border: `2px solid ${C.green}`,
                  borderRadius: 10, padding: 20, cursor: 'pointer',
                }}
                onClick={() => { setTrack('home'); setQStep(0); setQSelected(null); setGateBlocked(null); setPhase('questionnaire'); }}
              >
                <Flex align="center" gap={3} mb={2}>
                  <Text style={{ fontSize: 28 }}>🃏</Text>
                  <div>
                    <Text style={{ fontFamily: F.ui, fontSize: 16, fontWeight: 700, color: C.white }}>
                      Home Game Dealer
                    </Text>
                    <Badge colorScheme="green" fontSize="10px" letterSpacing="0.1em">ENTERTAINMENT FOCUS</Badge>
                  </div>
                </Flex>
                <Text style={{ color: C.ink, fontSize: 13, lineHeight: 1.6 }}>
                  Deal home games and casual events. Lenient certification, fully automated.
                  15-question test with 70% to pass.
                </Text>
              </div>

              {/* Professional Track */}
              <div
                style={{
                  background: '#0D1520', border: `2px solid ${C.gold}`,
                  borderRadius: 10, padding: 20, cursor: 'pointer',
                }}
                onClick={() => { setTrack('professional'); setQStep(0); setQSelected(null); setGateBlocked(null); setPhase('questionnaire'); }}
              >
                <Flex align="center" gap={3} mb={2}>
                  <Text style={{ fontSize: 28 }}>🎖</Text>
                  <div>
                    <Text style={{ fontFamily: F.ui, fontSize: 16, fontWeight: 700, color: C.white }}>
                      Freelance / Professional Dealer
                    </Text>
                    <Badge colorScheme="yellow" fontSize="10px" letterSpacing="0.1em">CAREER FOCUS</Badge>
                  </div>
                </Flex>
                <Text style={{ color: C.ink, fontSize: 13, lineHeight: 1.6 }}>
                  Deal paid events, connect with TDs, build a rated history.
                  Rigorous 30-question test at 85% to pass. Performance tracked.
                </Text>
              </div>
            </VStack>
          </div>
        </div>
      </Box>
    );
  }

  // ── QUESTIONNAIRE ──────────────────────────────────────────────────────
  if (phase === 'questionnaire') {
    const step = questionnaireSteps[qStep];
    const progress = ((qStep) / questionnaireSteps.length) * 100;

    if (gateBlocked) {
      return (
        <Box {...wrap}>
          <div style={{ ...cardStyle, border: `2px solid ${C.red}` }}>
            <div style={{ ...headerStyle(C.red), background: '#2D0D0D' }}>
              <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: '#FCA5A5', letterSpacing: '0.1em' }}>
                ⚠ ELIGIBILITY CHECK
              </Text>
            </div>
            <div style={{ padding: '28px 24px', textAlign: 'center' }}>
              <Text style={{ fontSize: 40, marginBottom: 16 }}>🚫</Text>
              <Text style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 12, fontFamily: F.ui }}>
                Not Eligible at This Time
              </Text>
              <Text style={{ color: C.ink, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
                {gateBlocked}
              </Text>
              <Flex gap={3}>
                <button style={btn(C.card, C.ink, C.border)} onClick={() => { setGateBlocked(null); setPhase('track_select'); setQStep(0); }}>
                  ← Choose Different Track
                </button>
                <button style={btn('#0D1F1E', C.teal, '#134E4A')} onClick={() => navigate('/certification')}>
                  Rules Certification →
                </button>
              </Flex>
            </div>
          </div>
        </Box>
      );
    }

    return (
      <Box {...wrap}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim }}>
            {track === 'home' ? 'Home Game' : 'Professional'} Application
          </Text>
          <Text style={{ fontFamily: F.mono, fontSize: 12, color: C.dim }}>
            {qStep + 1} / {questionnaireSteps.length}
          </Text>
        </Flex>
        <Progress value={progress} size="sm" colorScheme="teal" borderRadius="full" mb={4} />

        <div style={cardStyle}>
          <div style={headerStyle()}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.15em' }}>
              APPLICATION QUESTIONNAIRE
            </Text>
          </div>
          <div style={{ padding: '24px' }}>
            <Text style={{ color: C.white, fontSize: 17, fontWeight: 600, fontFamily: F.ui, lineHeight: 1.5, marginBottom: 20 }}>
              {step?.label}
            </Text>
            {(step as any)?.isTextInput ? (
              <>
                <input
                  type="text"
                  placeholder="e.g. Winnipeg, MB"
                  value={cityInput}
                  onChange={(e: any) => setCityInput(e.target.value)}
                  style={{
                    display: 'block', width: '100%', padding: '14px 16px',
                    background: '#1E2330', border: '2px solid #19E6CF',
                    borderRadius: 8, color: '#F0F4FF', fontSize: 16,
                    fontFamily: "'Rajdhani','Arial',sans-serif",
                    marginBottom: 12, boxSizing: 'border-box' as any,
                  }}
                />
                <button
                  style={{
                    ...btn(cityInput.trim() ? '#052E16' : C.card,
                          cityInput.trim() ? '#86EFAC' : C.dim,
                          cityInput.trim() ? '#166534' : C.border),
                    width: '100%', padding: '14px 20px', fontSize: 15,
                    opacity: cityInput.trim() ? 1 : 0.5,
                  }}
                  disabled={!cityInput.trim()}
                  onClick={() => {
                    const city = cityInput.trim();
                    if (!city) return;
                    const newAnswers = { ...qAnswers, city };
                    setQAnswers(newAnswers);
                    setCityInput('');
                    setQSelected(null);
                    if (qStep >= questionnaireSteps.length - 1) {
                      const qs = drawScreener();
                      setScreenerQs(qs);
                      setScreenerAnswers([]);
                      setScreenerIndex(0);
                      setSelectedAnswer(null);
                      setPhase('screener');
                    } else {
                      setQStep(q => q + 1);
                    }
                  }}
                >
                  Continue →
                </button>
              </>
            ) : (
              <>
                {step?.options.map((opt, i) => (
                  <div key={i} style={optionStyle(qSelected === i)} onClick={() => setQSelected(i)}>
                    {opt}
                  </div>
                ))}
                <button
                  style={{
                    ...btn(qSelected !== null ? '#052E16' : C.card, qSelected !== null ? '#86EFAC' : C.dim, qSelected !== null ? '#166534' : C.border),
                    width: '100%', marginTop: 8, opacity: qSelected === null ? 0.5 : 1,
                  }}
                  disabled={qSelected === null}
                  onClick={submitQStep}
                >
                  Continue →
                </button>
              </>
            )}
          </div>
        </div>
      </Box>
    );
  }

  // ── SCREENER ───────────────────────────────────────────────────────────
  if (phase === 'screener') {
    const q = screenerQs[screenerIndex];
    if (!q) return null;
    const progress = (screenerIndex / screenerQs.length) * 100;
    const diffColor = q.difficulty === 'hard' ? C.red : q.difficulty === 'medium' ? C.gold : C.green;

    return (
      <Box {...wrap}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim }}>Aptitude Screener</Text>
          <Text style={{ fontFamily: F.mono, fontSize: 12, color: C.dim }}>
            {screenerIndex + 1} / {screenerQs.length}
          </Text>
        </Flex>
        <Progress value={progress} size="sm" colorScheme="teal" borderRadius="full" mb={4} />

        <div style={cardStyle}>
          <div style={{ ...headerStyle(), justifyContent: 'space-between' }}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.15em' }}>
              APTITUDE SCREENER
            </Text>
            <span style={{ fontFamily: F.mono, fontSize: 10, color: diffColor, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              {q.difficulty}
            </span>
          </div>
          <div style={{ padding: '20px 24px 4px', borderBottom: `1px solid ${C.border}` }}>
            <Text style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: '0.15em', color: C.dim, textTransform: 'uppercase', marginBottom: 4 }}>
              {CATEGORY_NAMES[q.category] ?? q.category}
            </Text>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <Text style={{ color: C.white, fontSize: 16, fontWeight: 600, fontFamily: F.ui, lineHeight: 1.55, marginBottom: 20 }}>
              {q.question}
            </Text>
            {q.options.map((opt, i) => (
              <div key={i} style={optionStyle(selectedAnswer === i)} onClick={() => setSelectedAnswer(i)}>
                {opt}
              </div>
            ))}
            <button
              style={{
                ...btn('#052E16', '#86EFAC', '#166534'),
                width: '100%', marginTop: 8, opacity: selectedAnswer === null ? 0.5 : 1,
              }}
              disabled={selectedAnswer === null}
              onClick={submitScreenerAnswer}
            >
              Submit Answer
            </button>
          </div>
        </div>
      </Box>
    );
  }

  // ── SCREENER RESULT ────────────────────────────────────────────────────
  if (phase === 'screener_result' && screenerResult) {
    const { result, score, weakCategories, hardCorrect } = screenerResult;
    const pct = Math.round(score * 100);

    if (result === 'not_ready') {
      return (
        <Box {...wrap}>
          <div style={{ ...cardStyle, border: `2px solid ${C.red}` }}>
            <div style={{ background: '#2D0D0D', padding: '14px 24px', borderBottom: `1px solid ${C.border}` }}>
              <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: '#FCA5A5', letterSpacing: '0.15em' }}>
                READINESS CHECK COMPLETE
              </Text>
            </div>
            <div style={{ padding: '28px 24px', textAlign: 'center' }}>
              <Text style={{ fontSize: 40, marginBottom: 12 }}>📚</Text>
              <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700, color: C.white, marginBottom: 8 }}>
                We recommend studying before certifying
              </Text>
              <Text style={{ color: C.dim, fontSize: 14, marginBottom: 16, lineHeight: 1.7 }}>
                The readiness check (not the certification itself) showed some gaps in the fundamentals.
                Review the areas below, then come back to start your certification.
              </Text>
              {weakCategories.length > 0 && (
                <div style={{ background: '#0D1520', border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 20, textAlign: 'left' }}>
                  <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.gold, letterSpacing: '0.2em', marginBottom: 8 }}>
                    FOCUS YOUR STUDY ON:
                  </Text>
                  {weakCategories.map(cat => (
                    <Text key={cat} style={{ color: C.ink, fontSize: 13, lineHeight: 1.8 }}>
                      • {CATEGORY_NAMES[cat] ?? cat}
                    </Text>
                  ))}
                </div>
              )}
              <Flex gap={3}>
                <button style={btn(C.card, C.ink, C.border)} onClick={() => navigate('/certification')}>
                  Rules Cert →
                </button>
                <button
                  style={btn('#0D1520', C.gold, '#D4A017')}
                  onClick={() => {
                    setScreenerQs(drawScreener());
                    setScreenerAnswers([]);
                    setScreenerIndex(0);
                    setSelectedAnswer(null);
                    setPhase('screener');
                  }}
                >
                  Retry Screener
                </button>
              </Flex>
            </div>
          </div>
        </Box>
      );
    }

    return (
      <Box {...wrap}>
        <div style={{ ...cardStyle, border: `2px solid ${C.teal}` }}>
          <div style={{ background: '#0D1F1E', padding: '14px 24px', borderBottom: `1px solid ${C.border}` }}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.15em' }}>
              READINESS CHECK COMPLETE
            </Text>
          </div>
          <div style={{ padding: '28px 24px' }}>

            {/* What the screener is — clarify it is NOT the test */}
            <div style={{ background: '#0D1520', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 20 }}>
              <Text style={{ color: C.dim, fontSize: 13, lineHeight: 1.7 }}>
                The readiness check is <b style={{ color: C.ink }}>not a certification test</b> — it helps us
                route you to the right level. Your actual certification begins below.
              </Text>
            </div>

            {result === 'professional_ready' ? (
              <>
                <Flex align="center" gap={3} mb={12}>
                  <Text style={{ fontSize: 36 }}>🎖</Text>
                  <div>
                    <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700, color: C.white }}>
                      You're ready for the Professional track
                    </Text>
                    <Text style={{ color: C.dim, fontSize: 13 }}>
                      Strong result — {pct}% with {hardCorrect}/{SCREENER_CONFIG.hardCount} advanced questions correct
                    </Text>
                  </div>
                </Flex>
                <Text style={{ color: C.ink, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
                  You can go straight to the <b style={{ color: C.white }}>Professional Certification</b> (30 questions, 85% to pass).
                  Or take the Home Game Certification first as a warm-up — your choice.
                </Text>
              </>
            ) : (
              <>
                <Flex align="center" gap={3} mb={12}>
                  <Text style={{ fontSize: 36 }}>🃏</Text>
                  <div>
                    <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700, color: C.white }}>
                      Start with Home Game Certification
                    </Text>
                    <Text style={{ color: C.dim, fontSize: 13 }}>
                      Readiness check: {pct}% · {hardCorrect}/{SCREENER_CONFIG.hardCount} advanced questions correct
                    </Text>
                  </div>
                </Flex>
                <Text style={{ color: C.ink, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
                  Based on your readiness check, we recommend the <b style={{ color: C.white }}>Home Game Certification</b> as
                  your starting point. Once certified, you can upgrade to the Professional track.
                </Text>
              </>
            )}

            {weakCategories.length > 0 && (
              <div style={{ background: '#0D1520', border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.gold, letterSpacing: '0.2em', marginBottom: 8 }}>
                  SUGGESTED REVIEW BEFORE YOUR CERTIFICATION:
                </Text>
                {weakCategories.slice(0, 3).map(cat => (
                  <Text key={cat} style={{ color: C.ink, fontSize: 13, lineHeight: 1.8 }}>
                    • {CATEGORY_NAMES[cat] ?? cat}
                  </Text>
                ))}
              </div>
            )}

            <VStack gap={3} align="stretch">
              {result === 'professional_ready' && (
                <button style={{ ...btn('#052E16', '#86EFAC', '#166534'), width: '100%', padding: '14px 20px' }} onClick={() => startTest('professional')}>
                  🎖 Begin Professional Certification (30 questions · 85% to pass)
                </button>
              )}
              <button
                style={{ ...btn('#0A1A10', C.green, C.green), width: '100%', padding: '14px 20px' }}
                onClick={() => startTest('home')}
              >
                🃏 {result === 'professional_ready' ? 'Begin Home Game Certification Instead (15 questions · 70% to pass)' : 'Begin Home Game Certification (15 questions · 70% to pass)'}
              </button>
              {result !== 'professional_ready' && (
                <button
                  style={{ ...btn(C.card, C.dim, C.border), width: '100%', padding: '12px 20px', fontSize: 13 }}
                  onClick={() => startTest('professional')}
                >
                  🎖 Attempt Professional Certification instead (30 questions · 85% to pass)
                </button>
              )}
            </VStack>
          </div>
        </div>
      </Box>
    );
  }

  // ── TEST QUIZ ─────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = testQs[testIndex];
    if (!q) return null;
    const config = track === 'home' ? HOME_TEST_CONFIG : PROFESSIONAL_TEST_CONFIG;
    const progress = (testIndex / testQs.length) * 100;
    const diffColor = q.difficulty === 'hard' ? C.red : q.difficulty === 'medium' ? C.gold : C.green;

    return (
      <Box {...wrap}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim }}>
            {track === 'home' ? 'Home Game' : 'Professional'} Test · Attempt {attemptNumber}
          </Text>
          <Text style={{ fontFamily: F.mono, fontSize: 12, color: C.dim }}>
            {testIndex + 1} / {testQs.length}
          </Text>
        </Flex>
        <Progress value={progress} size="sm" colorScheme="teal" borderRadius="full" mb={4} />

        <div style={cardStyle}>
          <div style={{ ...headerStyle(), justifyContent: 'space-between' }}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.15em' }}>
              DEALER CERTIFICATION TEST
            </Text>
            <span style={{ fontFamily: F.mono, fontSize: 10, color: diffColor, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              {q.difficulty}
            </span>
          </div>
          <div style={{ padding: '12px 24px 4px', borderBottom: `1px solid ${C.border}` }}>
            <Text style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: '0.15em', color: C.dim, textTransform: 'uppercase' }}>
              {CATEGORY_NAMES[q.category] ?? q.category}
            </Text>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <Text style={{ color: C.white, fontSize: 16, fontWeight: 600, fontFamily: F.ui, lineHeight: 1.55, marginBottom: 20 }}>
              {q.question}
            </Text>
            {q.options.map((opt, i) => (
              <div key={i} style={optionStyle(selectedAnswer === i)} onClick={() => setSelectedAnswer(i)}>
                {opt}
              </div>
            ))}
            <button
              style={{
                ...btn('#052E16', '#86EFAC', '#166534'),
                width: '100%', marginTop: 8, opacity: selectedAnswer === null ? 0.5 : 1,
              }}
              disabled={selectedAnswer === null}
              onClick={submitTestAnswer}
            >
              Submit Answer
            </button>
          </div>
        </div>
      </Box>
    );
  }

  // ── WRONG ANSWER (show explanation) ──────────────────────────────────
  if (phase === 'wrong_answer' && wrongQ) {
    return (
      <Box {...wrap}>
        <div style={{ ...cardStyle, border: `2px solid #D97706` }}>
          <div style={{ background: '#78350F', padding: '14px 24px', borderBottom: `1px solid ${C.border}` }}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: '#FEF3C7', letterSpacing: '0.1em' }}>
              ⚠ INCORRECT — STUDY THIS RULE
            </Text>
          </div>
          <div style={{ padding: '24px' }}>
            <Text style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: '0.15em', color: C.dim, textTransform: 'uppercase', marginBottom: 8 }}>
              {CATEGORY_NAMES[wrongQ.category] ?? wrongQ.category}
            </Text>
            <Text style={{ color: C.white, fontSize: 16, fontWeight: 600, fontFamily: F.ui, lineHeight: 1.5, marginBottom: 16 }}>
              {wrongQ.question}
            </Text>
            <div style={{ background: '#0D1F1E', border: `1px solid #134E4A`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <Text style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: '0.2em', color: C.teal, marginBottom: 8 }}>
                THE CORRECT ANSWER
              </Text>
              <Text style={{ color: C.white, fontSize: 14, marginBottom: 8 }}>
                {wrongQ.options[wrongQ.correctIndex]}
              </Text>
              <Text style={{ fontFamily: F.mono, fontSize: 10, letterSpacing: '0.2em', color: C.teal, marginBottom: 8, marginTop: 12 }}>
                EXPLANATION
              </Text>
              <Text style={{ color: C.ink, fontSize: 13, lineHeight: 1.7 }}>
                {wrongQ.explanation}
              </Text>
            </div>
            <button
              style={{ ...btn('#052E16', '#86EFAC', '#166534'), width: '100%' }}
              onClick={() => {
                // Re-draw a fresh test so retry isn't the same questions
                const qs = track === 'home' ? drawHomeTest() : drawProfessionalTest();
                setTestQs(qs);
                setTestAnswers([]);
                setTestIndex(0);
                setSelectedAnswer(null);
                setAttemptNumber(a => a + 1);
                setWrongQ(null);
                setPhase('test');
              }}
            >
              I've studied it — retry test
            </button>
          </div>
        </div>
      </Box>
    );
  }

  // ── TEST FAILED ────────────────────────────────────────────────────────
  if (phase === 'test_result' && testResult) {
    const config = track === 'home' ? HOME_TEST_CONFIG : PROFESSIONAL_TEST_CONFIG;
    const pct = Math.round(testResult.score * 100);
    const attemptsLeft = config.maxAttempts === 999 ? '∞' : config.maxAttempts - attemptNumber;
    const canRetry = config.maxAttempts === 999 || attemptNumber < config.maxAttempts;

    return (
      <Box {...wrap}>
        <div style={{ ...cardStyle, border: `2px solid ${C.red}` }}>
          <div style={{ background: '#2D0D0D', padding: '14px 24px', borderBottom: `1px solid ${C.border}` }}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: '#FCA5A5', letterSpacing: '0.15em' }}>
              TEST RESULT · DID NOT PASS
            </Text>
          </div>
          <div style={{ padding: '28px 24px' }}>
            <Flex align="center" gap={12} mb={16}>
              <Text style={{ fontSize: 40 }}>📋</Text>
              <div>
                <Text style={{ fontFamily: F.ui, fontSize: 22, fontWeight: 700, color: C.white }}>
                  Score: {pct}%
                </Text>
                <Text style={{ color: C.dim, fontSize: 13 }}>
                  Required: {Math.round((track === 'home' ? HOME_TEST_CONFIG : PROFESSIONAL_TEST_CONFIG).passMark * 100)}%
                  · Attempt {attemptNumber}{config.maxAttempts !== 999 ? ` of ${config.maxAttempts}` : ''}
                </Text>
              </div>
            </Flex>

            {testResult.weakCategories.length > 0 && (
              <div style={{ background: '#0D1520', border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.gold, letterSpacing: '0.2em', marginBottom: 8 }}>
                  FOCUS YOUR REVIEW ON:
                </Text>
                {testResult.weakCategories.map((cat: string) => (
                  <Text key={cat} style={{ color: C.ink, fontSize: 13, lineHeight: 1.8 }}>
                    • {CATEGORY_NAMES[cat] ?? cat}
                  </Text>
                ))}
              </div>
            )}

            {canRetry ? (
              <button
                style={{ ...btn('#052E16', '#86EFAC', '#166534'), width: '100%', padding: '14px 20px' }}
                onClick={() => {
                  const qs = track === 'home' ? drawHomeTest() : drawProfessionalTest();
                  setTestQs(qs);
                  setTestAnswers([]);
                  setTestIndex(0);
                  setSelectedAnswer(null);
                  setAttemptNumber(a => a + 1);
                  setPhase('test');
                }}
              >
                Retry Test {config.maxAttempts !== 999 ? `(${attemptsLeft} attempts remaining)` : ''}
              </button>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <Text style={{ color: C.dim, fontSize: 14, marginBottom: 16 }}>
                  Maximum attempts reached. A {config.cooldownDays}-day cooldown applies before retrying.
                </Text>
                <button style={btn(C.card, C.ink, C.border)} onClick={() => navigate('/dashboard')}>
                  Return to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>
      </Box>
    );
  }

  // ── CERTIFIED ─────────────────────────────────────────────────────────
  if (phase === 'certified') {
    const badge = TIER_BADGES[tier];
    const isPending = (qAnswers as any).conduct_issue;

    return (
      <Box {...wrap}>
        <div style={{ ...cardStyle, border: `2px solid ${C.gold}` }}>
          <div style={{ background: '#1A0F00', padding: '14px 24px', borderBottom: `1px solid ${C.border}` }}>
            <Text style={{ fontFamily: F.ui, fontSize: 13, fontWeight: 700, color: C.gold, letterSpacing: '0.2em' }}>
              ★ DEALER CERTIFICATION COMPLETE
            </Text>
          </div>
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            <Text style={{ fontSize: 52, marginBottom: 12 }}>{badge.icon}</Text>
            <Heading style={{ fontFamily: F.ui, color: C.white, fontSize: 26, marginBottom: 8 }}>
              {badge.label}
            </Heading>
            {isPending ? (
              <div style={{ background: '#2D1F00', border: `1px solid ${C.gold}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <Text style={{ color: C.gold, fontSize: 13, lineHeight: 1.7 }}>
                  Your application is under review due to your conduct disclosure.
                  You will be notified once your Dealer Passport is activated.
                </Text>
              </div>
            ) : (
              <Text style={{ color: C.dim, fontSize: 14, marginBottom: 24, lineHeight: 1.7 }}>
                Your Dealer Passport is now active. TDs can find your profile, and your badge
                appears on your Player Passport.
              </Text>
            )}
            <Flex gap={3}>
              <Button flex={1} variant="outline" colorScheme="teal" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
              {!isPending && (
                <Button flex={1} colorScheme="yellow" onClick={() => user && navigate(`/dealer/${user.uid}`)}>
                  View Dealer Passport
                </Button>
              )}
            </Flex>
          </div>
        </div>
      </Box>
    );
  }

  return null;
};

export default DealerCertification;
